import { useSSE } from '@client/components/hooks/chatpage-hooks/useSSE';
import { Utils, memoryOptimizer } from '@client/utility';
import type { CustomApiConfig } from '@components';

interface GenerateOptions {
  user_uuid: string;
  chat_uuid: string;
  char_uuid: string;
  messageRefId: number;
  message_type: 'char' | 'user';
  generationType: 'Generate' | 'Regenerate';
  isFirstMessage?: boolean;
  custom_api_config?: CustomApiConfig;
}

interface SSECallbacks {
  onChunk?: (chunk: string, fullText: string) => void;
  onMessage?: (data: any) => void;
  onDone?: (finalText: string) => void;
  onError?: () => void;
  onStop?: () => void;
}

export const useGenerateAPI = () => {
  const { startSSE, stopSSE } = useSSE();

  const generate = async (
    options: GenerateOptions,
    callbacks: SSECallbacks = {}
  ) => {
    const tokenResp = await Utils.post<{ turn_token: string }>(
      '/api/CreateToken',
      {
        user_uuid: options.user_uuid,
        chat_uuid: options.chat_uuid,
        char_uuid: options.char_uuid,
        ref_id: options.messageRefId,
        message: {
          type: options.message_type,
          is_first: !!options.isFirstMessage,
        },
      }
    );

    const turn_token = tokenResp?.turn_token;
    if (!turn_token) throw new Error('Failed to get turn token');

    const body: Record<string, any> = {
      type: options.generationType,
      chat_uuid: options.chat_uuid,
      turn_token,
    };

    if (options.custom_api_config) {
      body.custom_api_config = options.custom_api_config;
    }

    const url = `/api/Generate?user_uuid=${options.user_uuid}`;

    await startSSE(url, body, {
      onMessage: (data) => {
        callbacks.onMessage?.(data);
        memoryOptimizer.optimize();
      },
      onChunk: (chunk, fullText) => {
        callbacks.onChunk?.(chunk, fullText);
        memoryOptimizer.optimize();
      },
      onDone: (finalText) => {
        callbacks.onDone?.(finalText);
        memoryOptimizer.optimize();
      },
      onError: () => {
        callbacks.onError?.();
        memoryOptimizer.emergencyCleanup();
      },
      onStop: () => {
        callbacks.onStop?.();
        memoryOptimizer.clearTraces();
      },
    });
  };

  const stop = (callbacks?: SSECallbacks) => stopSSE(callbacks);

  return { generate, stop };
};